import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { IconKeys } from '@powerfleet/shared/common/enums';
import { ROOT_PATH } from '@powerfleet/shared/injection-tokens';
import { IconCacheService } from '@powerfleet/shared/utils';
import { IconSettingsService } from '@progress/kendo-angular-icons';
import { SVGIcon } from '@progress/kendo-svg-icons';

type IconOverride = {
  kendoKey: string;
  svgKey: IconKeys;
};

@Injectable({
  providedIn: 'root',
})
export class KendoIconsService extends IconSettingsService {
  private readonly iconCacheService = inject(IconCacheService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly rootPath = inject(ROOT_PATH);

  private readonly overrides: IconOverride[] = [
    { kendoKey: 'caret-alt-up', svgKey: IconKeys.chevron_up },
    { kendoKey: 'caret-alt-down', svgKey: IconKeys.chevron_down },
    { kendoKey: 'caret-alt-left', svgKey: IconKeys.chevron_left },
    { kendoKey: 'caret-alt-right', svgKey: IconKeys.chevron_right },
    { kendoKey: 'chevron-left', svgKey: IconKeys.chevron_left },
    { kendoKey: 'chevron-right', svgKey: IconKeys.chevron_right },
    { kendoKey: 'chevron-up', svgKey: IconKeys.chevron_up },
    { kendoKey: 'arrow-chevron-up', svgKey: IconKeys.chevron_up },
    { kendoKey: 'chevron-down', svgKey: IconKeys.chevron_down },
    { kendoKey: 'x-circle', svgKey: IconKeys.close },
    { kendoKey: 'x', svgKey: IconKeys.close },
    { kendoKey: 'close', svgKey: IconKeys.close },
    { kendoKey: 'plus', svgKey: IconKeys.add },
    { kendoKey: 'minus', svgKey: IconKeys.minus },
    { kendoKey: 'columns', svgKey: IconKeys.columns },
    { kendoKey: 'more-vertical', svgKey: IconKeys.actions },
    { kendoKey: 'filter', svgKey: IconKeys.filter },
    { kendoKey: 'sort-asc-small', svgKey: IconKeys.arrow_up },
    { kendoKey: 'sort-desc-small', svgKey: IconKeys.arrow_down },
    { kendoKey: 'caret-alt-to-left', svgKey: IconKeys.pin_left },
    { kendoKey: 'caret-alt-to-right', svgKey: IconKeys.pin_right },
    { kendoKey: 'cancel', svgKey: IconKeys.close },
    { kendoKey: 'clock', svgKey: IconKeys.chevron_down },
    { kendoKey: 'palette', svgKey: IconKeys.palette },
    { kendoKey: 'upload', svgKey: IconKeys.upload },
    { kendoKey: 'file-image', svgKey: IconKeys.add_image },
    { kendoKey: 'checkmark', svgKey: IconKeys.checkmark },
    { kendoKey: 'color-canvas', svgKey: IconKeys.gradient },
    { kendoKey: 'window', svgKey: IconKeys.full_screen },
    { kendoKey: 'window-minimize', svgKey: IconKeys.minus },
    { kendoKey: 'window-restore', svgKey: IconKeys.picture_in_picture },
  ];

  private readonly svgDictionary: Record<string, SVGIcon> = {};

  public async init(): Promise<void> {
    let loadedCount = 0;

    for (const icon of this.overrides) {
      const loaded = await this.load(icon.kendoKey, icon.svgKey);
      if (loaded) {
        loadedCount++;
      }
    }

    if (loadedCount === 0 && isPlatformBrowser(this.platformId)) {
      console.warn('Kendo icon overrides: no custom SVGs were loaded.');
    }

    // Notify existing icon wrappers to re-evaluate icon settings after overrides are populated.
    this.notify();
  }

  public override getSvgIcon(svgIconName: string): SVGIcon {
    return this.svgDictionary[svgIconName] ?? super.getSvgIcon(svgIconName);
  }

  private async load(kendoKey: string, svgKey: IconKeys): Promise<boolean> {
    const data = await this.iconCacheService.getSVG(svgKey) ?? await this.fetchSvgDirect(svgKey);
    if (!data) {
      return false;
    }

    const parsedSvg = this.parseSvg(data);
    if (!parsedSvg) {
      return false;
    }

    this.svgDictionary[kendoKey] = {
      name: kendoKey,
      content: parsedSvg.content,
      viewBox: parsedSvg.viewBox,
    };

    return true;
  }

  private async fetchSvgDirect(svgKey: IconKeys): Promise<string | undefined> {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }

    try {
      const response = await fetch(`${this.rootPath}assets/icons/${svgKey}.svg`);
      if (!response.ok) {
        return undefined;
      }

      return await response.text();
    } catch {
      return undefined;
    }
  }

  private parseSvg(data: string): { content: string; viewBox: string } | undefined {
    const svgMatch = data.match(/<svg\b([^>]*)>([\s\S]*?)<\/svg>/i);
    if (!svgMatch) {
      return undefined;
    }

    const attrs = svgMatch[1] ?? '';
    const content = svgMatch[2] ?? '';
    const viewBoxMatch = attrs.match(/viewBox\s*=\s*(['"])(.*?)\1/i);
    const viewBox = viewBoxMatch?.[2] ?? '0 0 24 24';

    return { content, viewBox };
  }
}